<script setup>
import AddTenantsToServerAction from '@frontJs/components/actions/projectManagement/dev/AddTenantsToServerAction.vue';
import RemoveTenantToServerAction from '@frontJs/components/actions/projectManagement/dev/RemoveTenantToServerAction.vue';
import SaveTenantHasServerAdditionalInformationAction from '@frontJs/components/actions/projectManagement/dev/SaveTenantHasServerAdditionalInformationAction.vue';
import FileTextIcon from '@iconify-vue/ph/file-text';
import PencilSimpleIcon from '@iconify-vue/ph/pencil-simple';
import PlusIcon from '@iconify-vue/ph/plus';
import WarningIcon from '@iconify-vue/ph/warning';
import XIcon from '@iconify-vue/ph/x';
import DisplayContentAction from '@js/components/actions/DisplayContentAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import LinkCell from '@js/components/tables/cells/LinkCell.vue';
import StatusCell from '@js/components/tables/cells/StatusCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { formatDate } from '@js/utils/formatter';
import { useRoute } from '@js/utils/route';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { server } = defineProps({
    server: {
        type: Object,
        required: true,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const phpVersionMaintainedUntilDates = [
    {
        version: '5.4',
        maintainedUntil: '2015-09-03',
    },
    {
        version: '5.6',
        maintainedUntil: '2018-12-31',
    },
    {
        version: '7.0',
        maintainedUntil: '2019-01-10',
    },
    {
        version: '7.1',
        maintainedUntil: '2019-12-01',
    },
    {
        version: '7.2',
        maintainedUntil: '2020-11-30',
    },
    {
        version: '7.3',
        maintainedUntil: '2021-12-06',
    },
    {
        version: '7.4',
        maintainedUntil: '2022-11-28',
    },
    {
        version: '8.0',
        maintainedUntil: '2023-11-26',
    },
    {
        version: '8.1',
        maintainedUntil: '2025-12-31',
    },
    {
        version: '8.2',
        maintainedUntil: '2026-12-31',
    },
    {
        version: '8.3',
        maintainedUntil: '2027-12-31',
    },
    {
        version: '8.4',
        maintainedUntil: '2028-12-31',
    },
];

const {
    loading,
    table,
    count,
    globalFilter,
    filters,
    availableFilters,
    setGlobalFilter,
    setFilters,
    updateData,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'name',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Nom',
                }),
            cell: (props) =>
                h(LinkCell, {
                    text: props.row.original.name,
                    href: route(
                        'front.projectManagement.customer.updateCustomer',
                        {
                            tenant: props.row.original.id,
                        },
                    ),
                    target: '_self',
                    inertia: true,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'cloudflarePackageId',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Cloudflare',
                }),
            cell: (props) =>
                h(StatusCell, {
                    status:
                        props.row.original.cloudflarePackage ||
                        props.row.original.freeCloudflare ||
                        props.row.original.proCloudflare,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'phpVersion',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'V. PHP',
                }),
            cell: (props) =>
                props.row.original.phpVersion
                    ? h(TextCell, {
                          text: props.row.original.phpVersion,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'phpVersion',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: "V. PHP maintenue jusqu'à",
                }),
            cell: (props) =>
                props.row.original.phpVersion
                    ? h(TextCell, {
                          text: formatDate(
                              phpVersionMaintainedUntilDates.find(
                                  (item) =>
                                      item.version ===
                                      props.row.original.phpVersion,
                              ).maintainedUntil,
                          ),
                          tooltipContent:
                              new Date(
                                  phpVersionMaintainedUntilDates.find(
                                      (item) =>
                                          item.version ===
                                          props.row.original.phpVersion,
                                  ).maintainedUntil,
                              ) < new Date()
                                  ? 'La version PHP <b>' +
                                    props.row.original.phpVersion +
                                    "</b> n'est plus maintenue."
                                  : null,
                          tooltipIcon: WarningIcon,
                          tooltipPlacement: 'top',
                          tooltipColor: 'red',
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'otherServers',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Autres serveurs',
                }),
            cell: (props) =>
                props.row.original.otherServers &&
                props.row.original.otherServers.length > 0
                    ? h(TextCell, {
                          text: props.row.original.otherServers
                              .map((server) => server.name)
                              .join(', '),
                      })
                    : null,
            enableSorting: false,
        }),
        columnHelper.display({
            id: 'details',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Détails',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () =>
                            props.row.original.additionalInformation
                                ? h(
                                      DisplayContentAction,
                                      {
                                          title: 'Infos complémentaires',
                                          content:
                                              props.row.original
                                                  .additionalInformation,
                                      },
                                      {
                                          trigger: () =>
                                              h(ActionButton, {
                                                  size: 'small',
                                                  square: true,
                                                  title: 'Voir les informations complémentaires',
                                                  type: 'primary',
                                                  icon: FileTextIcon,
                                              }),
                                      },
                                  )
                                : null,
                    },
                ),
        }),
        columnHelper.display({
            id: 'actions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actions',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () => [
                            h(
                                SaveTenantHasServerAdditionalInformationAction,
                                {
                                    server,
                                    tenant: props.row.original,
                                    callback: updateData,
                                },
                                {
                                    trigger: () =>
                                        h(ActionButton, {
                                            size: 'small',
                                            square: true,
                                            title: 'Enregistrer les informations complémentaires',
                                            icon: PencilSimpleIcon,
                                            type: 'primary',
                                        }),
                                },
                            ),
                            h(
                                RemoveTenantToServerAction,
                                {
                                    server,
                                    tenant: props.row.original,
                                    callback: updateData,
                                },
                                {
                                    button: ({ action }) =>
                                        h(ActionButton, {
                                            size: 'small',
                                            square: true,
                                            type: 'danger',
                                            title: 'Retirer',
                                            icon: XIcon,
                                            callback: () => action(),
                                        }),
                                },
                            ),
                        ],
                    },
                ),
        }),
    ],
    url: route('front.projectManagement.dev.postTenantsServer'),
    data: {
        serverId: server.id,
    },
    columnPinning: {
        right: ['actions'],
    },
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :filters="filters"
        :available-filters="availableFilters"
        :set-filters="setFilters"
        :set-global-filter="setGlobalFilter"
        :loading="loading"
    >
        <template #actions>
            <AddTenantsToServerAction :server="server" :callback="updateData">
                <template #trigger>
                    <ActionButton
                        size="small"
                        :left-icon="PlusIcon"
                        type="primary"
                    >
                        Ajouter un client
                    </ActionButton>
                </template>
            </AddTenantsToServerAction>
        </template>
    </DataTableLayout>
</template>
